import { announce } from '@react-aria/live-announcer';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import useIsFaved from '@/hooks/app/useIsFaved';
import { useUserState } from '@/hooks/store';
import { calmLogger } from '@/utils/calmLogger';
import heartIconFilled from 'icons/heart-filled.svg';
import heartIconOutline from 'icons/heart-outline.svg';

import messages from './messages';
import { Wrapper } from './styles';
import { FaveHeartProps } from './types';

const FaveHeart = ({ withBorder, guideId, guideTitle, shouldShowText }: FaveHeartProps) => {
	const apiRequest = useApi();
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();

	const user = useUserState();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const [isFaved, setIsFaved] = useIsFaved(guideId);

	const onPress = async (): Promise<void> => {
		// The save would 401 for a signed-out visitor, so prompt rather than flip the heart locally.
		if (!user) {
			setIsModalOpen(true);
			return;
		}

		const addToFaves = !isFaved;
		setIsFaved(addToFaves);

		const messageToAnnounce = addToFaves ? messages.isFavedAnnouncement : messages.isUnfavedAnnouncement;

		announce(
			formatMessage(messageToAnnounce, {
				title: guideTitle,
			}),
		);

		try {
			await apiRequest({
				endpoint: `programs/guides/${guideId}/faves`,
				method: addToFaves ? 'POST' : 'DELETE',
			});

			logEvent({
				eventName: 'Fave Button : Fave : Posted',
				eventProps: {
					program_title: guideId,
				},
			});
		} catch (err) {
			calmLogger.error('Error favoriting content', {}, err);
		}
	};

	return (
		<Wrapper
			data-testid="fave-heart"
			aria-label={isFaved ? 'unmark as favorite' : 'mark as favorite'}
			aria-pressed={isFaved}
			onPress={onPress}
			$isFaved={isFaved}
			$withBorder={withBorder}
			Icon={isFaved ? heartIconFilled : heartIconOutline}
		>
			{shouldShowText && formatMessage(messages.favoriteCTA)}
		</Wrapper>
	);
};

export default FaveHeart;
